<template>
  <div class="portal-home">
    <!-- 公共头部组件 -->
    <CommonHeader />

    <!-- Search Section -->
    <div class="search-section">
      <div class="search-container">
        <div class="search-bar">
          <div
            v-if="!showInlineIntellectualPropertyQuery"
            class="search-label"
          >
            知产查询
          </div>
          <BackButton
            v-else
            @click="handleBack"
          />
          <div class="search-input-wrapper">
            <el-icon class="search-icon">
              <Search />
            </el-icon>
            <el-input
              v-model="searchQuery"
              placeholder="限中国大陆依法注册登记的法人主体"
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <div class="filter-options">
              <div
                v-for="(filter, key) in filterOptions"
                :key="key"
                class="filter-item"
                :class="{ active: filters[key] }"
                @click="toggleFilter(key)"
              >
                <span
                  v-if="filters[key]"
                  class="check-icon"
                >✓</span>
                <span class="filter-text">{{ filter }}</span>
              </div>
            </div>
            <el-button
              class="search-btn"
              @click="handleSearch"
            >
              查询
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- 在查询按钮下方内联展示知识产权查询结果（与设计图一致） -->
    <transition
      name="fade-slide"
      mode="out-in"
    >
      <div
        v-if="showInlineIntellectualPropertyQuery"
        key="query"
        ref="inlineQueryRef"
        class="inline-query-wrapper"
      >
        <IntellectualPropertyQuery
          :search-params="searchParams"
          :auto-run-query="inlineIpQueryAutoRun"
        />
      </div>

      <div
        v-else
        key="main"
        class="main-content-wrapper"
      >
        <!-- 导航菜单：左右箭头轮播切换，支持十几个入口 -->
        <div
          class="nav-menu"
          :class="{ 'nav-menu--carousel': shouldUseNavCarousel }"
        >
          <div class="nav-menu-scroll-shell">
            <button
              v-show="shouldUseNavCarousel"
              type="button"
              class="nav-scroll-btn nav-scroll-btn--prev"
              :disabled="!navScrollState.showPrev"
              aria-label="向左切换导航"
              @click="scrollNavMenu('prev')"
            >
              <el-icon><ArrowLeft /></el-icon>
            </button>
            <div class="nav-menu-viewport">
              <div
                ref="navMenuContainerRef"
                class="nav-menu-container"
                @scroll="updateNavScrollState"
              >
                <button
                  v-for="(item, index) in navItems"
                  :key="item"
                  class="nav-btn"
                  :class="{ active: activeNav === item }"
                  @click="handleNavClick(item)"
                >
                  <img
                    v-if="index === 0"
                    src="@/assets/icons/frame.png"
                    alt=""
                    class="nav-icon nav-icon-img"
                  >
                  <span>{{ item }}</span>
                </button>
              </div>
            </div>
            <button
              v-show="shouldUseNavCarousel"
              type="button"
              class="nav-scroll-btn nav-scroll-btn--next"
              :disabled="!navScrollState.showNext"
              aria-label="向右切换导航"
              @click="scrollNavMenu('next')"
            >
              <el-icon><ArrowRight /></el-icon>
            </button>
          </div>
        </div>

        <!-- 二级菜单：手机/iPad 溢出时显示左右箭头 -->
        <div
          v-if="activeNav === '版权保护' || activeNav === '版权服务' || activeNav === '版权服务机构' || activeNav === '全球版权资讯'"
          class="secondary-menu"
          :class="{ 'secondary-menu--carousel': shouldUseSecondaryCarousel }"
        >
          <div class="secondary-menu-scroll-shell">
            <button
              v-show="shouldUseSecondaryCarousel"
              type="button"
              class="nav-scroll-btn nav-scroll-btn--prev"
              :disabled="!secondaryScrollState.showPrev"
              aria-label="向左切换二级菜单"
              @click="scrollSecondaryMenu('prev')"
            >
              <el-icon><ArrowLeft /></el-icon>
            </button>
            <div class="secondary-menu-viewport">
              <div
                ref="secondaryMenuContainerRef"
                class="secondary-menu-container"
                @scroll="updateSecondaryScrollState"
              >
                <button
                  v-for="(item, index) in currentSecondaryItems"
                  :key="index"
                  class="secondary-menu-btn"
                  :class="{ active: activeSecondaryMenu === item }"
                  @click="handleSecondaryMenuClick(item)"
                >
                  {{ item }}
                </button>
              </div>
            </div>
            <button
              v-show="shouldUseSecondaryCarousel"
              type="button"
              class="nav-scroll-btn nav-scroll-btn--next"
              :disabled="!secondaryScrollState.showNext"
              aria-label="向右切换二级菜单"
              @click="scrollSecondaryMenu('next')"
            >
              <el-icon><ArrowRight /></el-icon>
            </button>
          </div>
        </div>

        <!-- 主内容：由路由渲染子页面，与 user-center 一致 -->
        <router-view v-slot="{ Component }">
          <component
            :is="Component"
            :key="routerViewKey"
            :search-params="searchParams"
          />
        </router-view>

        <CommonFooter @link-click="handleFooterLinkClick" />
      </div>
    </transition>

    <LoginAuthDialog
      v-model="showLoginAuthDialog"
      @confirm="handleLoginAuthConfirm"
      @cancel="handleLoginAuthCancel"
    />
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Search, ArrowLeft, ArrowRight } from '@element-plus/icons-vue'
import CommonHeader from '@/views/commonHeaderAndFooter/header.vue'
import CommonFooter from '@/views/commonHeaderAndFooter/footer.vue'
import LoginAuthDialog from '@/components/LoginAuthDialog/index.vue'
import IntellectualPropertyQuery from '@/views/copyrightProtection/IntellectualPropertyQuery.vue'
import BackButton from '@/components/BackButton.vue'
import { isLoggedIn, isIdentityVerified, hasPersonalType, getCurrentMainBodyName } from '@/utils/auth'
import { showEnhancedAlertDialog } from '@/utils/messageUtils'
import { CERTIFICATE_TYPE, CERTIFICATE_TYPE_DISPLAY } from '@/enums/CertificateTypeEnum'
export default {
  name: 'PortalHome',
  components: {
    CommonHeader,
    CommonFooter,
    LoginAuthDialog,
    IntellectualPropertyQuery,
    Search,
    BackButton,
    ArrowLeft,
    ArrowRight
  },
  data() {
    return {
      // 证书类型常量（暴露给模板使用）
      CERTIFICATE_TYPE,
      CERTIFICATE_TYPE_DISPLAY,
      searchQuery: '',
      filters: {
        [CERTIFICATE_TYPE.WORK_COPYRIGHT]: false,
        [CERTIFICATE_TYPE.SOFTWARE_COPYRIGHT]: false,
        [CERTIFICATE_TYPE.TRADEMARK]: false,
        [CERTIFICATE_TYPE.PATENT]: false,
        [CERTIFICATE_TYPE.DIGITAL_FILING]: false,
        all: false
      },
      filterOptions: {
        [CERTIFICATE_TYPE.WORK_COPYRIGHT]: CERTIFICATE_TYPE_DISPLAY[CERTIFICATE_TYPE.WORK_COPYRIGHT],
        [CERTIFICATE_TYPE.SOFTWARE_COPYRIGHT]: CERTIFICATE_TYPE_DISPLAY[CERTIFICATE_TYPE.SOFTWARE_COPYRIGHT],
        [CERTIFICATE_TYPE.TRADEMARK]: '商标', // 首页使用简化名称
        [CERTIFICATE_TYPE.PATENT]: '专利', // 首页使用简化名称
        // [CERTIFICATE_TYPE.DIGITAL_FILING]: CERTIFICATE_TYPE_DISPLAY[CERTIFICATE_TYPE.DIGITAL_FILING],
        all: '全部'
      },
      navItems: [
        '为你推荐',
        '版权保护',
        '版权服务',
        '授权交易',
        '教育培训',
        '全球版权资讯',
        '行业交流&活动',
        '法规&资助政策',
        '版权产业分布',
        '版权服务机构'
      ],
      // 主导航横向滚动状态（仅 iPad/手机溢出时启用箭头）
      navScrollState: {
        showPrev: false,
        showNext: false,
        scrollable: false
      },
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1440, // 当前视口宽度
      // 二级菜单横向滚动状态（仅 iPad/手机溢出时启用箭头）
      secondaryScrollState: {
        showPrev: false,
        showNext: false,
        scrollable: false
      },
      // 版权保护二级菜单项
      copyrightProtectionItems: [
        '原创作品备案',
        '产品发布存证',
        '版权预审',
        '知识产权管理',
        '版权风险监测',
        '自助取证/固证',
        '合同模版',
        '免费咨询'
      ],
      // 版权服务二级菜单
      copyrightServiceItems: [
        '作品著作权登记',
        '软件著作权登记',
        'TSA证据固化',
        '知识产权纠纷调解',
        '版权鉴定比对',
        '版权合规辅导',
        '版权培训合作',
        '平台侵权申诉'
      ],
      // 版权服务机构二级菜单
      copyrightServiceOrganizationsItems: [
        '版权社会组织',
        '（优秀）版权专业律所',
        '（优秀）版权咨询服务机构',
      ],
      // 全球版权资讯二级菜单
      globalCopyrightNewsItems: [
        '全球专业资讯',
        '全球授权资讯',
        '全球前沿报告',
        '深度解析'
      ],
      // 授权交易二级菜单
      authorizationTradingItems: [
        '找IP',
        '找品牌',
        '找供应链'
      ],
      // 登录验证弹窗显示状态
      showLoginAuthDialog: false,
      // 查询参数（传递给知识产权查询组件）
      searchParams: {
        keyword: '',
        filters: {}
      },
      // 是否在本页内联展示知识产权查询（用于点击“查询”后的设计图效果）
      showInlineIntellectualPropertyQuery: false,
      /** 内联知产查询是否根据 searchParams 自动拉列表；从内链入口打开时为 false，仅展示界面 */
      inlineIpQueryAutoRun: true
    }
  },
  computed: {
    // 根据当前路由 path 计算一级导航（与 user-center 的 activeTab 方式一致）
    activeNav() {
      const path = this.$route.path
      if (path === '/' || path.includes('/recommendation')) return '为你推荐'
      if (path.includes('/education')) return '教育培训'
      if (path.includes('/copyright-protection')) return '版权保护'
      if (path.includes('/copyright-service-organizations')) return '版权服务机构'
      if (path.includes('/copyright-services')) return '版权服务'
      if (path.includes('/authorization-trading')) return '授权交易'
      if (path.includes('/global-copyright-news')) return '全球版权资讯'
      if (path.includes('/industry-communication-events')) return '行业交流&活动'
      if (path.includes('/regulations-funding-policies')) return '法规&资助政策'
      if (path.includes('/copyright-industry-distribution')) return '版权产业分布'
      return '为你推荐'
    },
    // 根据当前路由 path 计算二级菜单选中项
    activeSecondaryMenu() {
      const path = this.$route.path
      if (this.activeNav === '版权保护') {
        if (path.includes('original-work-filing')) return '原创作品备案'
        if (path.includes('product-launch-filing')) return '产品发布存证'
        if (path.includes('intellectual-property-management')) return '知识产权管理'
        if (path.includes('evidence-collection')) return '自助取证/固证'
        if (path.includes('copyright-pre-examination')) return '版权预审'
        if (path.includes('copyright-risk-monitoring')) return '版权风险监测'
        if (path.includes('contract-templates')) return '合同模版'
        if (path.includes('free-consultation')) return '免费咨询'
        return this.copyrightProtectionItems[0]
      }
      if (this.activeNav === '版权服务') {
        if (path.includes('/copyright-services/work-copyright-registration')) return '作品著作权登记'
        if (path.includes('/copyright-services/software-copyright-registration')) return '软件著作权登记'
        if (path.includes('/copyright-services/tsa-evidence-solidification')) return 'TSA证据固化'
        if (path.includes('/copyright-services/ip-dispute-mediation')) return '知识产权纠纷调解'
        if (path.includes('/copyright-services/copyright-appraisal-comparison')) return '版权鉴定比对'
        if (path.includes('/copyright-services/copyright-compliance-guidance')) return '版权合规辅导'
        if (path.includes('/copyright-services/copyright-training-cooperation')) return '版权培训合作'
        if (path.includes('/copyright-services/platform-infringement-complaint')) return '平台侵权申诉'
        return this.copyrightServiceItems[0]
      }
      if (this.activeNav === '版权服务机构') {
        if (path.includes('copyright-social-organization')) return '版权社会组织'
        if (path.includes('excellent-copyright-law-firms')) return '（优秀）版权专业律所'
        if (path.includes('excellent-copyright-consulting-agencies')) return '（优秀）版权咨询服务机构'
        return this.copyrightServiceOrganizationsItems[0]
      }
      if (this.activeNav === '全球版权资讯') {
        if (path.includes('professional-news')) return '全球专业资讯'
        if (path.includes('licensing-news')) return '全球授权资讯'
        if (path.includes('frontier-report')) return '全球前沿报告'
        if (path.includes('deep-analysis')) return '深度解析'
        return '全球前沿报告'
      }
      if (this.activeNav === '授权交易') {
        if (path.includes('/find-brand')) return '找品牌'
        if (path.includes('/find-supply')) return '找供应链'
        return '找IP'
      }
      return ''
    },
    // 一级导航 -> 跳转路径（与 user-center 的 handleTabClick 方式一致）
    navItemToPath() {
      return {
        '为你推荐': '/',
        '版权保护': '/home/copyright-protection/original-work-filing',
        '版权服务': '/home/copyright-services/work-copyright-registration',
        '授权交易': '/home/authorization-trading/find-ip',
        '教育培训': '/home/education',
        '全球版权资讯': '/home/global-copyright-news/frontier-report',
        '行业交流&活动': '/home/industry-communication-events',
        '法规&资助政策': '/home/regulations-funding-policies',
        '版权产业分布': '/home/copyright-industry-distribution',
        '版权服务机构': '/home/copyright-service-organizations/copyright-social-organization'
      }
    },
    // 二级菜单项 -> 跳转路径
    secondaryMenuToPath() {
      return {
        '原创作品备案': '/home/copyright-protection/original-work-filing',
        '产品发布存证': '/home/copyright-protection/product-launch-filing',
        '版权预审': '/home/copyright-protection/copyright-pre-examination',
        '知识产权管理': '/home/copyright-protection/intellectual-property-management',
        '版权风险监测': '/home/copyright-protection/copyright-risk-monitoring',
        '自助取证/固证': '/home/copyright-protection/evidence-collection',
        '合同模版': '/home/copyright-protection/contract-templates',
        '免费咨询': '/home/copyright-protection/free-consultation',
        '版权社会组织': '/home/copyright-service-organizations/copyright-social-organization',
        '（优秀）版权专业律所': '/home/copyright-service-organizations/excellent-copyright-law-firms',
        '（优秀）版权咨询服务机构': '/home/copyright-service-organizations/excellent-copyright-consulting-agencies',
        '作品著作权登记': '/home/copyright-services/work-copyright-registration',
        '软件著作权登记': '/home/copyright-services/software-copyright-registration',
        'TSA证据固化': '/home/copyright-services/tsa-evidence-solidification',
        '知识产权纠纷调解': '/home/copyright-services/ip-dispute-mediation',
        '版权鉴定比对': '/home/copyright-services/copyright-appraisal-comparison',
        '版权合规辅导': '/home/copyright-services/copyright-compliance-guidance',
        '版权培训合作': '/home/copyright-services/copyright-training-cooperation',
        '平台侵权申诉': '/home/copyright-services/platform-infringement-complaint',
        '全球专业资讯': '/home/global-copyright-news/professional-news',
        '全球授权资讯': '/home/global-copyright-news/licensing-news',
        '全球前沿报告': '/home/global-copyright-news/frontier-report',
        '深度解析': '/home/global-copyright-news/deep-analysis',
        '找IP': '/home/authorization-trading/find-ip',
        '找品牌': '/home/authorization-trading/find-brand',
        '找供应链': '/home/authorization-trading/find-supply'
      }
    },
    currentSecondaryItems() {
      if (this.activeNav === '版权保护') return this.copyrightProtectionItems
      if (this.activeNav === '版权服务') return this.copyrightServiceItems
      if (this.activeNav === '版权服务机构') return this.copyrightServiceOrganizationsItems
      if (this.activeNav === '全球版权资讯') return this.globalCopyrightNewsItems
      if (this.activeNav === '授权交易') return this.authorizationTradingItems
      return []
    },
    routerViewKey() {
      const resetRouteNames = ['OriginalWorkFiling', 'ProductLaunchFiling']
      return resetRouteNames.includes(this.$route.name) ? this.$route.path : this.$route.name
    },
    // 仅 iPad/手机端内容溢出时启用左右箭头（PC 始终均分占满整行）
    shouldUseNavCarousel() {
      if (this.viewportWidth > 1024) return false
      return this.navScrollState.scrollable
    },
    // 二级菜单：仅 iPad/手机溢出时显示左右箭头（PC 保持居中）
    shouldUseSecondaryCarousel() {
      if (this.viewportWidth > 1024) return false
      return this.secondaryScrollState.scrollable
    }
  },
  watch: {
    // 导航项数量变化时，重新计算是否需要横向滚动
    navItems: {
      handler() {
        this.$nextTick(() => {
          this.updateNavScrollState()
          this.initNavMenuResizeObserver()
        })
      },
      deep: true
    },
    shouldUseNavCarousel() {
      this.$nextTick(() => {
        this.updateNavScrollState()
      })
    },
    shouldUseSecondaryCarousel() {
      this.$nextTick(() => {
        this.updateSecondaryScrollState()
      })
    },
    activeNav() {
      this.$nextTick(() => {
        this.updateSecondaryScrollState()
        this.initSecondaryMenuResizeObserver()
      })
    },
    currentSecondaryItems: {
      handler() {
        this.$nextTick(() => {
          this.updateSecondaryScrollState()
          this.initSecondaryMenuResizeObserver()
        })
      },
      deep: true
    },
    // 内联知产查询打开时 router-view 被隐藏；从顶栏等入口路由跳转时需先关闭内联区，否则子页面无法渲染
    '$route.path'(newPath, oldPath) {
      if (newPath !== oldPath && this.showInlineIntellectualPropertyQuery) {
        this.showInlineIntellectualPropertyQuery = false
      }
      this.inlineIpQueryAutoRun = true
      this.$nextTick(() => {
        this.tryOpenPendingInlineIpQuery()
      })
    }
  },
  mounted() {
    this.tryOpenPendingInlineIpQuery()
    // 监听token过期事件
    const handleAuthExpired = () => {
      this.showLoginDialog = true
      // 注意：错误消息已在 request.js 的 handle401Error 中统一显示，这里不需要重复显示
    }
    window.addEventListener('auth-expired', handleAuthExpired)
    window.addEventListener('resize', this.handleViewportResize)

    // 保存事件处理器，用于销毁时移除
    this._authExpiredHandler = handleAuthExpired

    this.$nextTick(() => {
      this.handleViewportResize()
      this.initNavMenuResizeObserver()
      this.initSecondaryMenuResizeObserver()
    })
  },
  beforeUnmount() {
    // 移除事件监听
    if (this._authExpiredHandler) {
      window.removeEventListener('auth-expired', this._authExpiredHandler)
      this._authExpiredHandler = null
    }
    window.removeEventListener('resize', this.handleViewportResize)
    this.destroyNavMenuResizeObserver()
    this.destroySecondaryMenuResizeObserver()
  },
  methods: {
    /** 同步视口宽度并更新主导航、二级菜单滚动状态 */
    handleViewportResize() {
      if (typeof window !== 'undefined') {
        this.viewportWidth = window.innerWidth
      }
      this.updateNavScrollState()
      this.updateSecondaryScrollState()
    },
    /** 初始化主导航尺寸监听，内容变化时重新判断是否需要轮播 */
    initNavMenuResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      const container = this.$refs.navMenuContainerRef
      if (!container) return
      this.destroyNavMenuResizeObserver()
      this._navMenuResizeObserver = new ResizeObserver(() => {
        this.handleViewportResize()
      })
      this._navMenuResizeObserver.observe(container)
    },
    /** 销毁主导航尺寸监听 */
    destroyNavMenuResizeObserver() {
      if (this._navMenuResizeObserver) {
        this._navMenuResizeObserver.disconnect()
        this._navMenuResizeObserver = null
      }
    },
    /** 初始化二级菜单尺寸监听 */
    initSecondaryMenuResizeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      const container = this.$refs.secondaryMenuContainerRef
      if (!container) return
      this.destroySecondaryMenuResizeObserver()
      this._secondaryMenuResizeObserver = new ResizeObserver(() => {
        this.updateSecondaryScrollState()
      })
      this._secondaryMenuResizeObserver.observe(container)
    },
    /** 销毁二级菜单尺寸监听 */
    destroySecondaryMenuResizeObserver() {
      if (this._secondaryMenuResizeObserver) {
        this._secondaryMenuResizeObserver.disconnect()
        this._secondaryMenuResizeObserver = null
      }
    },
    /** 更新主导航轮播箭头显示状态 */
    updateNavScrollState() {
      const container = this.$refs.navMenuContainerRef
      if (!container) return

      // PC 端保持原样：均分占满整行，不启用箭头
      if (this.viewportWidth > 1024) {
        this.navScrollState.scrollable = false
        this.navScrollState.showPrev = false
        this.navScrollState.showNext = false
        if (container.scrollLeft > 0) {
          container.scrollLeft = 0
        }
        return
      }

      const buttons = Array.from(container.querySelectorAll('.nav-btn'))
      if (!buttons.length) return

      const containerStyle = window.getComputedStyle(container)
      const gap = parseFloat(containerStyle.columnGap || containerStyle.gap) || 10
      let contentWidth = 0
      buttons.forEach((btn, index) => {
        contentWidth += btn.scrollWidth
        if (index > 0) contentWidth += gap
      })

      const scrollable = contentWidth > container.clientWidth + 4
      this.navScrollState.scrollable = scrollable

      if (!scrollable) {
        this.navScrollState.showPrev = false
        this.navScrollState.showNext = false
        if (container.scrollLeft > 0) {
          container.scrollLeft = 0
        }
        return
      }

      const maxScrollLeft = container.scrollWidth - container.clientWidth
      this.navScrollState.showPrev = container.scrollLeft > 4
      this.navScrollState.showNext = container.scrollLeft < maxScrollLeft - 4
    },
    /** 点击左右箭头切换主导航（按可视区域翻页） */
    scrollNavMenu(direction) {
      const container = this.$refs.navMenuContainerRef
      if (!container) return
      const step = Math.max(container.clientWidth - 24, 160)
      const delta = direction === 'next' ? step : -step
      container.scrollBy({ left: delta, behavior: 'smooth' })
      window.setTimeout(() => {
        this.updateNavScrollState()
      }, 320)
    },
    /** 更新二级菜单轮播箭头显示状态 */
    updateSecondaryScrollState() {
      const container = this.$refs.secondaryMenuContainerRef
      if (!container) return

      if (this.viewportWidth > 1024) {
        this.secondaryScrollState.scrollable = false
        this.secondaryScrollState.showPrev = false
        this.secondaryScrollState.showNext = false
        if (container.scrollLeft > 0) {
          container.scrollLeft = 0
        }
        return
      }

      const buttons = Array.from(container.querySelectorAll('.secondary-menu-btn'))
      if (!buttons.length) return

      const containerStyle = window.getComputedStyle(container)
      const gap = parseFloat(containerStyle.columnGap || containerStyle.gap) || 10
      let contentWidth = 0
      buttons.forEach((btn, index) => {
        contentWidth += btn.scrollWidth
        if (index > 0) contentWidth += gap
      })

      const scrollable = contentWidth > container.clientWidth + 4
      this.secondaryScrollState.scrollable = scrollable

      if (!scrollable) {
        this.secondaryScrollState.showPrev = false
        this.secondaryScrollState.showNext = false
        if (container.scrollLeft > 0) {
          container.scrollLeft = 0
        }
        return
      }

      const maxScrollLeft = container.scrollWidth - container.clientWidth
      this.secondaryScrollState.showPrev = container.scrollLeft > 4
      this.secondaryScrollState.showNext = container.scrollLeft < maxScrollLeft - 4
    },
    /** 点击左右箭头切换二级菜单 */
    scrollSecondaryMenu(direction) {
      const container = this.$refs.secondaryMenuContainerRef
      if (!container) return
      const step = Math.max(container.clientWidth - 24, 140)
      const delta = direction === 'next' ? step : -step
      container.scrollBy({ left: delta, behavior: 'smooth' })
      window.setTimeout(() => {
        this.updateSecondaryScrollState()
      }, 320)
    },
    refreshPage() {
      window.location.reload()
    },
   async getCurrentUserInfo() {
      const response = await this.$auth.refreshMainBody()
      console.log('response', response)
    },
    /**
     * 知识产权管理弹窗「去查询」：进入首页后仅展开内联查询区（不自动请求列表），查询逻辑与用户点击首页「查询」一致。
     */
    tryOpenPendingInlineIpQuery() {
      if (this.$route.query.open !== 'open') {
        return
      }

      // 移除 URL 中的标记，避免刷新页面时重复触发
      const query = { ...this.$route.query }
      delete query.open
      this.$router.replace({ query }).catch(() => {})

      if (!isLoggedIn()) {
        this.showLoginAuthDialog = true
        return
      }
      if (!isIdentityVerified()) {
        ElMessage.warning('请先完成实名认证后再进行知产查询')
        this.showLoginAuthDialog = true
        return
      }

      this.inlineIpQueryAutoRun = false
      this.searchParams = {
        keyword: (this.searchQuery || '').trim(),
        filters: { ...this.filters }
      }
      this.showInlineIntellectualPropertyQuery = true
    },
    // 处理查询按钮点击
    handleSearch() {

      // 检查是否已登录
      if (!isLoggedIn()) {
        // 未登录，显示登录验证提示弹窗
        this.showLoginAuthDialog = true
        return
      }

      // 检查是否已完成实名认证
      const verified = isIdentityVerified()
      if (!verified) {
        ElMessage.warning('请先完成实名认证后再进行知产查询')
        this.showLoginAuthDialog = true
        return
      }

      if (hasPersonalType()) {
        showEnhancedAlertDialog('该功能仅限企业账号使用，请用企业账号操作。', '提示', '确定')
        return
      }

      // 验证查询关键词不能为空
      if (!this.searchQuery || !this.searchQuery.trim()) {
        ElMessage.warning('知产查询关键词不能为空！')
        return
      }

      const keyword = this.searchQuery.trim()
      const currentMainBodyName = (getCurrentMainBodyName() || '').trim()
      if (keyword !== currentMainBodyName) {
        showEnhancedAlertDialog('只能查询自己企业的知产', '提示', '确定')
        return
      }

      // 验证必须至少选择一个证书类型
      if (!Object.keys(this.filters).some(key => this.filters[key] === true)) {
        ElMessage.warning('请至少选择一个知产类型！')
        return
      }


      this.inlineIpQueryAutoRun = true
      this.showInlineIntellectualPropertyQuery = true
      console.log('this.filters', this.filters)
      this.searchParams = {
        keyword: keyword,
        filters: { ...this.filters }
      }
    },
    // 处理搜索框获取焦点
    handleSearchFocus() {
      // 如果未登录或未实名认证，允许用户输入，但不展开查询页面和弹窗
      if (!isLoggedIn() || !isIdentityVerified()) {
        return
      }

      // 如果当前还没有展示查询页面，则展示它（不自动触发查询请求）
      if (!this.showInlineIntellectualPropertyQuery) {
        this.inlineIpQueryAutoRun = false
        this.searchParams = {
          keyword: (this.searchQuery || '').trim(),
          filters: { ...this.filters }
        }
        this.showInlineIntellectualPropertyQuery = true
      }
    },
    // 处理登录验证弹窗确认
    handleLoginAuthConfirm() {
      // 用户点击"去登录"，组件内部会跳转到登录页面
      this.showLoginAuthDialog = false
    },
    // 处理登录验证弹窗取消
    handleLoginAuthCancel() {
      // 用户点击"暂不登录"，直接关闭弹窗
      this.showLoginAuthDialog = false
    },
    // 切换筛选选项
    toggleFilter(key) {
      if (key === 'all') {
        // 如果点击"全部"，勾选所有筛选选项
        const allSelected = this.filters.all
        Object.keys(this.filters).forEach(k => {
          this.filters[k] = !allSelected
        })
      } else {
        // 取消"全部"选项
        this.filters.all = false
        // 切换当前选项
        this.filters[key] = !this.filters[key]
      }
    },
    // 返回上一页（关闭内联查询）
    handleBack() {
      this.showInlineIntellectualPropertyQuery = false
    },
    // 处理导航菜单点击：使用路由跳转（与 user-center handleTabClick 一致）
    handleNavClick(item) {
      this.showInlineIntellectualPropertyQuery = false
      this.inlineIpQueryAutoRun = true
      const path = this.navItemToPath[item] || '/'
      this.$router.push(path)
    },
    // 处理二级菜单点击：使用路由跳转（与 user-center handleMenuClick 一致）
    handleSecondaryMenuClick(item) {
      this.showInlineIntellectualPropertyQuery = false
      this.inlineIpQueryAutoRun = true
      const path = this.secondaryMenuToPath[item] || '/home/placeholder'
      this.$router.push(path)
    },
    // 处理底部链接点击
    handleFooterLinkClick(link) {
      console.log('底部链接点击:', link)
    }
  }
}
</script>

<style lang="scss" scoped>
.portal-home {
  min-height: 100vh;
  padding-top: 84px; /* 为固定头部预留高度（与 header 一致） */
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
}

// 动画效果
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(15px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}

.main-content-wrapper {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
}

// 内联查询区域：尽量贴近搜索按钮展示
.inline-query-wrapper {
  width: 100%;
  background: #fff;
}

.inline-query-wrapper :deep(.intellectual-property-query) {
  min-height: auto;
  padding-top: 0; // 嵌入门户首页时避免额外留白
}

// Search Section - 优化搜索区域样式
.search-section {
  background: transparent;
  padding: 30px 0;

  .search-container {
    max-width: 1400px;
    margin: 0 auto;
    padding: 0 30px;
  }

  .search-bar {
    display: flex;
    align-items: center;
    gap: 16px;

    .search-label {
      font-size: 15px;
      color: #333;
      font-weight: bold;
      white-space: nowrap;
    }

    .search-input-wrapper {
      flex: 1;
      position: relative;
      background-color: #f9f9f9;
      border-radius: 50px;
      padding: 6px 20px;
      display: flex;
      align-items: center;
      gap: 16px;

      .search-icon {
        position: absolute;
        left: 20px;
        color: #999;
        font-size: 14px;
        z-index: 1;
      }

      .search-input {
        flex: 1;

        :deep(.el-input__wrapper) {
          background-color: transparent !important;
          border: none !important;
          box-shadow: none !important;

          &.is-focus {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }

          &:hover {
            background-color: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
        }

        :deep(.el-input__inner) {
          height: 23px;
          font-size: 14px;
          font-weight: 500;
          border-radius: 18px;
          border: none !important;
          outline: none !important;
          background-color: transparent !important;
          padding-left: 8px;
          padding-right: 0;
          color: #000;
          box-shadow: none !important;

          &::placeholder {
            color: #999;
            font-weight: 500;
          }

          &:focus {
            background-color: transparent !important;
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
            color: #000;
          }

          &:hover {
            background-color: transparent !important;
            border: none !important;
          }
        }
      }
    }

    .filter-options {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-shrink: 0;
      margin-left: auto;

      .filter-item {
        display: flex;
        align-items: center;
        gap: 5px;
        padding: 5px 10px;
        border-radius: 16px;
        border: 1px solid transparent;
        cursor: pointer;
        transition: all 0.2s;
        position: relative;

        .check-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          background-color: #000;
          color: #fff;
          border-radius: 2px;
          font-size: 11px;
          font-weight: bold;
        }

        .filter-text {
          font-size: 13px;
          color: #666;
          font-weight: 500;
        }

        &.active {
          background: #ffffff;
          border-radius: 16px;
          border: 1px solid #e8e8e8;

          .filter-text {
            color: #000;
            font-weight: 500;
          }
        }

        &:not(.active) {
          .check-icon {
            display: none;
          }

          .filter-text {
            color: #666;
          }
        }
      }
    }

    .search-btn {
      font-family: PingFang SC, PingFang SC;
      font-weight: 500;
      font-size: 14px;
      color: #0d62f3;
      text-align: center;
      font-style: normal;
      text-transform: none;
      background: #ffffff;
      box-shadow: 0px 4px 12px 0px rgba(25, 25, 25, 0.07);
      border-radius: 16px;
      border: 1px solid transparent;
      padding: 8px 20px;
      height: 36px;
      cursor: pointer;
      transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
      flex-shrink: 0;

      &:hover {
        background: #ffffff;
        border-color: #0d62f3;
        box-shadow: 0px 6px 16px 0px rgba(13, 98, 243, 0.2);
      }

      &:active {
        background: #f8faff;
        border-color: #0d62f3;
        box-shadow: 0px 2px 8px 0px rgba(13, 98, 243, 0.15);
      }
    }
  }
}

// Navigation Menu - 无上间距，仅下间距 20px
.nav-menu {
  padding: 0 0 20px 0;
  position: relative;
  width: 100%;
  box-sizing: border-box;

  .nav-menu-scroll-shell {
    max-width: 1460px;
    width: 100%;
    margin: 0 auto;
    padding: 0 30px;
    box-sizing: border-box;
  }

  .nav-menu-viewport {
    width: 100%;
  }

  .nav-menu-container {
    box-sizing: border-box;
    width: 100%;
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    gap: 10px;
    overflow-x: visible;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .nav-scroll-btn {
    display: none;
  }

  &.nav-menu--carousel {
    .nav-menu-scroll-shell {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .nav-menu-viewport {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    .nav-menu-container {
      justify-content: flex-start;
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      scroll-snap-type: x proximity;
    }

    .nav-btn {
      flex: 0 0 auto;
      width: auto;
      min-width: 120px;
      white-space: nowrap;
      scroll-snap-align: start;
    }
  }

  .nav-btn {
    flex: 1;
    min-width: 0;
    font-family: PingFang SC, PingFang SC;
    font-weight: 500;
    font-size: 16px;
    color: #ffffff;
    text-align: center;
    font-style: normal;
    text-transform: none;
    background: #2c2c2c;
    border-radius: 6px;
    border: none;
    padding: 10px 18px;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2px;
    position: relative;
    z-index: 1;
    overflow: hidden;
    box-sizing: border-box;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-image: url('@/assets/images/navigationBackground.png');
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      opacity: 0.3;
      z-index: -1;
    }

    &:hover {
      background: rgba(35, 35, 35, 0.85);

      &::before {
        opacity: 0.4;
      }
    }

    &.active {
      background: #0D62F3;
      color: #ffffff;
      font-weight: 600;

      &::before {
        opacity: 0.2;
      }

      
    }

    span {
      line-height: 1.2;
    }

    .nav-icon {
      display: flex;
      align-items: center;
    }

    .nav-icon-img {
      width: 16px;
      height: 16px;
      object-fit: contain;
      flex-shrink: 0;
    }
  }

}

// 主导航/二级菜单共用轮播箭头
.nav-scroll-btn {
  display: none;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 1px solid #dcdfe6;
  border-radius: 50%;
  background: #ffffff;
  color: #303133;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, opacity 0.2s ease, border-color 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f7fa;
    border-color: #0d62f3;
    color: #0d62f3;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.nav-menu--carousel .nav-scroll-btn,
.secondary-menu--carousel .nav-scroll-btn {
  display: inline-flex;
}

// Secondary Menu - 二级菜单样式（无上下间距）
.secondary-menu {
  background-color: #ffffff;
  padding: 0;
  width: 100%;
  box-sizing: border-box;

  .secondary-menu-scroll-shell {
    max-width: 1400px;
    width: 100%;
    margin: 0 auto;
    padding: 0 30px;
    box-sizing: border-box;
  }

  .secondary-menu-viewport {
    width: 100%;
  }

  .secondary-menu-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    overflow-x: visible;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &.secondary-menu--carousel {
    .secondary-menu-scroll-shell {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .secondary-menu-viewport {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    .secondary-menu-container {
      justify-content: flex-start;
      flex-wrap: nowrap;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-behavior: smooth;
      scroll-snap-type: x proximity;
      scroll-padding-inline: 4px;
    }
  }

  .secondary-menu-btn {
    background-color: #ffffff;
    color: #333333;
    border: 1px solid #e8e8e8;
    padding: 6px 14px;
    font-size: 13px;
    white-space: nowrap;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    flex-shrink: 0;
    scroll-snap-align: start;
    font-family: inherit;
    font-weight: 400;
    min-height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    line-height: 1.5;
    box-sizing: border-box;

    &:hover {
      background-color: #f5f5f5;
      border-color: #d0d0d0;
    }

    &.active {
      background-color: #e6f3ff;
      color: #0d63f3;
      border-color: #0d63f3;
      font-weight: 500;
    }
  }
}

// 手机/iPad 主导航尺寸微调（≤1024px，溢出时才出现箭头）
@media (max-width: 1024px) {
  .nav-menu {
    padding: 0 0 16px 0;

    .nav-menu-scroll-shell {
      padding: 0 20px;
    }

    &.nav-menu--carousel .nav-menu-scroll-shell {
      gap: 8px;
    }

    .nav-menu-container {
      gap: 8px;
    }

    &.nav-menu--carousel .nav-menu-container {
      scroll-padding-inline: 4px;
    }

    &.nav-menu--carousel .nav-scroll-btn {
      width: 32px;
      height: 32px;
    }

    &.nav-menu--carousel .nav-btn {
      min-width: 108px;
      min-height: 42px;
      padding: 10px 14px;
      font-size: 14px;
    }

    &:not(.nav-menu--carousel) .nav-btn {
      flex: 1 1 0;
      min-width: 0;
      min-height: 42px;
      padding: 10px 10px;
      font-size: 14px;
      white-space: nowrap;
    }
  }
}

// Responsive Design
// iPad/手机搜索区公共内边距（≤1024px）
@media (max-width: 1024px) {
  .search-section {
    padding: 25px 0;

    .search-container {
      padding: 0 20px;
    }
  }
}

// iPad 搜索区（769px - 1024px）：与 PC 相同的一行布局
@media (max-width: 1024px) and (min-width: 769px) {
  .portal-home {
    padding-top: 72px;
  }

  .search-section {
    .search-bar {
      flex-direction: row;
      align-items: center;
      gap: 12px;

      .search-label {
        flex-shrink: 0;
        font-size: 14px;
        text-align: left;
      }

      .search-input-wrapper {
        position: relative;
        display: flex;
        flex: 1;
        min-width: 0;
        align-items: center;
        gap: 12px;
        padding: 6px 16px 6px 36px;
        border-radius: 50px;

        .search-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          flex-shrink: 0;
          z-index: 1;
          pointer-events: none;
        }

        .search-input {
          flex: 1;
          min-width: 0;

          :deep(.el-input__wrapper) {
            padding-left: 4px;
            min-height: 32px;
          }

          :deep(.el-input__inner) {
            height: 32px;
            line-height: 32px;
            font-size: 13px;
            padding-left: 0;
            text-overflow: ellipsis;
          }
        }

        .filter-options {
          flex-shrink: 0;
          margin-left: auto;
          flex-wrap: nowrap;
          gap: 10px;

          .filter-item {
            flex-shrink: 0;
            white-space: nowrap;
            padding: 4px 8px;

            .filter-text {
              white-space: nowrap;
              font-size: 12px;
            }
          }
        }

        .search-btn {
          flex-shrink: 0;
          width: auto;
          height: 32px;
          margin-left: 0;
          padding: 4px 16px;
          border-radius: 16px;
          font-size: 13px;
        }
      }
    }
  }

  .secondary-menu {
    padding: 0;

    .secondary-menu-scroll-shell {
      padding: 0 20px;
    }

    &.secondary-menu--carousel .secondary-menu-scroll-shell {
      gap: 6px;
    }

    &.secondary-menu--carousel .nav-scroll-btn {
      width: 30px;
      height: 30px;
      flex-shrink: 0;
    }

    .secondary-menu-container {
      gap: 8px;
    }
  }
}

// 移动端适配 (≤ 768px，含 iPhone)
@media (max-width: 768px) {
  .portal-home {
    padding-top: 64px;
    overflow-x: hidden;
  }

  .search-section {
    padding: 20px 0;

    .search-container {
      padding: 0 15px;
    }

    // 手机端两行：第一行搜索框+按钮，第二行筛选标签
    .search-bar {
      flex-direction: row;
      align-items: center;
      gap: 8px;

      .search-label {
        flex-shrink: 0;
        font-size: 12px;
        align-self: center;
      }

      .search-input-wrapper {
        flex: 1;
        min-width: 0;
        display: grid;
        grid-template-columns: auto 1fr auto;
        grid-template-rows: auto auto;
        align-items: center;
        gap: 6px 8px;
        padding: 8px 10px;
        border-radius: 12px;

        .search-icon {
          position: static;
          grid-column: 1;
          grid-row: 1;
          transform: none;
          flex-shrink: 0;
          font-size: 13px;
          z-index: 1;
          pointer-events: none;
        }

        .search-input {
          grid-column: 2;
          grid-row: 1;
          min-width: 0;
          padding-left: 0;

          :deep(.el-input__wrapper) {
            min-height: 30px;
            padding-left: 0;
          }

          :deep(.el-input__inner) {
            font-size: 11px;
            height: 30px;
            line-height: 30px;
            padding-left: 0;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
          }
        }

        .search-btn {
          grid-column: 3;
          grid-row: 1;
          flex-shrink: 0;
          height: 30px;
          padding: 4px 12px;
          font-size: 11px;
          margin-left: 0;
        }

        .filter-options {
          grid-column: 1 / -1;
          grid-row: 2;
          width: 100%;
          margin-left: 0;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          gap: 6px;
          padding-top: 2px;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;

          &::-webkit-scrollbar {
            display: none;
          }

          .filter-item {
            flex-shrink: 0;
            white-space: nowrap;
            padding: 3px 6px;

            .check-icon {
              width: 11px;
              height: 11px;
              font-size: 9px;
            }

            .filter-text {
              white-space: nowrap;
              font-size: 11px;
            }
          }
        }
      }
    }
  }

  .nav-menu {
    padding: 0 0 14px 0;

    .nav-menu-scroll-shell {
      padding: 0 15px;
    }

    &.nav-menu--carousel .nav-menu-scroll-shell {
      gap: 6px;
    }

    &.nav-menu--carousel .nav-scroll-btn {
      width: 30px;
      height: 30px;
    }

    &.nav-menu--carousel .nav-btn {
      min-width: 100px;
      min-height: 44px;
      padding: 10px 12px;
      font-size: 13px;
      border-radius: 6px;
      gap: 6px;

      .nav-icon-img {
        width: 14px;
        height: 14px;
      }
    }

    &:not(.nav-menu--carousel) .nav-btn {
      flex: 1 1 0;
      min-width: 0;
      min-height: 44px;
      padding: 10px 8px;
      font-size: 13px;
      white-space: nowrap;

      .nav-icon-img {
        width: 14px;
        height: 14px;
      }
    }
  }

  .secondary-menu {
    padding: 0 0 4px 0;

    .secondary-menu-scroll-shell {
      padding: 0 15px;
    }

    &.secondary-menu--carousel .secondary-menu-scroll-shell {
      gap: 6px;
    }

    &.secondary-menu--carousel .nav-scroll-btn {
      width: 28px;
      height: 28px;
    }

    .secondary-menu-container {
      gap: 8px;
    }

    .secondary-menu-btn {
      padding: 8px 12px;
      font-size: 12px;
      min-height: 34px;
      border-radius: 6px;
    }
  }
}

// 小屏幕手机适配 (≤ 480px)
@media (max-width: 480px) {
  .portal-home {
    padding-top: 60px;
  }

  .search-section {
    padding: 15px 0;

    .search-container {
      padding: 0 12px;
    }

    // 小屏手机两行布局尺寸微调
    .search-bar {
      gap: 6px;

      .search-label {
        font-size: 11px;
      }

      .search-input-wrapper {
        gap: 5px 6px;
        padding: 6px 8px;
        border-radius: 10px;

        .search-icon {
          font-size: 12px;
        }

        .search-input {
          :deep(.el-input__wrapper) {
            min-height: 28px;
          }

          :deep(.el-input__inner) {
            font-size: 10px;
            height: 28px;
            line-height: 28px;
          }
        }

        .filter-options {
          gap: 4px;

          .filter-item {
            padding: 2px 5px;

            .check-icon {
              width: 10px;
              height: 10px;
              font-size: 8px;
            }

            .filter-text {
              font-size: 10px;
            }
          }
        }

        .search-btn {
          height: 28px;
          padding: 3px 10px;
          font-size: 10px;
        }
      }
    }
  }

  .nav-menu {
    padding: 0 0 12px 0;

    .nav-menu-scroll-shell {
      padding: 0 12px;
    }

    &.nav-menu--carousel .nav-menu-scroll-shell {
      gap: 6px;
    }

    &.nav-menu--carousel .nav-scroll-btn {
      width: 28px;
      height: 28px;
    }

    &.nav-menu--carousel .nav-btn {
      min-width: 92px;
      min-height: 42px;
      padding: 8px 10px;
      font-size: 12px;
      border-radius: 5px;
      gap: 4px;

      .nav-icon-img {
        width: 13px;
        height: 13px;
      }
    }

    &:not(.nav-menu--carousel) .nav-btn {
      padding: 8px 6px;
      font-size: 12px;
      min-height: 42px;
    }
  }

  .secondary-menu {
    padding: 0 0 4px 0;

    .secondary-menu-scroll-shell {
      padding: 0 12px;
    }

    &.secondary-menu--carousel .nav-scroll-btn {
      width: 26px;
      height: 26px;
    }

    .secondary-menu-btn {
      padding: 7px 10px;
      font-size: 11px;
      min-height: 32px;
    }
  }
}

</style>
